import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mdiAccountBox, mdiExitToApp } from "@mdi/js";
import Icon from "@mdi/react";
import { Avatar, Dropdown, Tooltip } from "antd";
import { observer } from "mobx-react-lite";

import { logout } from "../../api/auth";
import { fetchGravatarUrl } from "../../api/gravatar";
import i18n, { browserLang } from "../../i18n";
import { loginStore } from "../../stores/loginStore";
import { signupStore } from "../../stores/signupStore";
import { userStore } from "../../stores/userStore";
import { sendCounterEvent } from "../../utils/counterService";
import { usePendingAction } from "../../utils/usePendingAction";

import "./UserMenu.css";

export const UserMenu = observer(function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [gravatarUrl, setGravatarUrl] = useState<string | null>(null);
  const { pending: signingOut, run: runSignOut } = usePendingAction();

  const handleLogout = async () => {
    await runSignOut(async () => {
      sendCounterEvent("user_icon_logout");
      userStore.clear();
      loginStore.clear();
      signupStore.clear();
      localStorage.clear();
      i18n.changeLanguage(browserLang);
      try {
        await logout();
      } catch {
        // session may already be gone
      }
      navigate("/login/1", { replace: true });
    });
  };

  const email = userStore.profile?.email ?? "";

  useEffect(() => {
    if (!email) return;
    fetchGravatarUrl(email)
      .then(setGravatarUrl)
      .catch(() => {});
  }, [email]);

  if (!userStore.profile) return null;

  const overlay = (
    <div className="user-menu__dropdown">
      <div className="user-menu__header">
        <div className="user-menu__caption">{userStore.caption}</div>
        <div className="user-menu__email">{email}</div>
      </div>
      <div className="user-menu__divider" />
      <a
        className="user-menu__item"
        href="/profile"
        onClick={() => {
          sendCounterEvent("user_icon_profile");
          setOpen(false);
        }}
      >
        <Icon path={mdiAccountBox} size={0.9} color="rgba(0,0,0,0.45)" />
        {t("userMenu.profile")}
      </a>
      <button
        className="user-menu__item"
        disabled={signingOut}
        aria-busy={signingOut}
        onClick={handleLogout}
      >
        <Icon path={mdiExitToApp} size={0.9} color="rgba(0,0,0,0.45)" />
        {t("userMenu.signOut")}
      </button>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={(newOpen) => {
        if (newOpen) sendCounterEvent("header_user_icon");
        setOpen(newOpen);
      }}
      popupRender={() => overlay}
      placement="bottomRight"
      align={{ offset: [0, 14] }}
      trigger={["click"]}
    >
      <Tooltip
        title={t("header.profileTooltip")}
        open={open ? false : undefined}
      >
        <span className="user-menu__avatar-wrap">
          <Avatar
            className="user-menu__avatar"
            size={40}
            src={gravatarUrl ?? undefined}
            style={{
              backgroundImage: gravatarUrl
                ? undefined
                : "linear-gradient(135deg, #0070C5 0%, #42A5F5 100%)",
              color: "#fff",
              cursor: "pointer",
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            {userStore.initials}
          </Avatar>
        </span>
      </Tooltip>
    </Dropdown>
  );
});
